import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { get } from '../services/ApiService';
import { selectUser } from './selector';

const PermissionsContext = createContext(null);

const FOCUS_REFRESH_MIN_INTERVAL_MS = 60_000;

const CANONICAL_KEY_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
const isUsableSeed = (arr) =>
    Array.isArray(arr) && arr.length > 0 && arr.every((p) => typeof p === 'string' && CANONICAL_KEY_RE.test(p));

export const usePermissions = () => {
    const ctx = useContext(PermissionsContext);
    if (!ctx) {
        throw new Error('usePermissions must be used within a PermissionsProvider');
    }
    return ctx;
};

export const PermissionsProvider = ({ children }) => {
    const user = useSelector(selectUser);
    const userId = user?._id || null;
    const seedPerms = user?.userPermissions;

    const [permissions, setPermissions] = useState(() =>
        isUsableSeed(seedPerms) ? seedPerms : [],
    );
    const [loading, setLoading] = useState(false);
    const [isReady, setIsReady] = useState(() => isUsableSeed(seedPerms));
    const [error, setError] = useState(null);

    const generationRef = useRef(0);
    const lastFetchAtRef = useRef(0);

    const loadPermissions = useCallback(async () => {
        const myGen = ++generationRef.current;

        if (!userId) {
            if (myGen !== generationRef.current) return;
            setPermissions([]);
            setLoading(false);
            setError(null);
            setIsReady(true);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await get('api/auth/my-permissions');
            if (myGen !== generationRef.current) return;

            if (response?.success) {
                setPermissions(response.data?.permissions || []);
                setError(null);
            } else {
                setError(response?.error || 'failed_to_load_permissions');
            }
        } catch (err) {
            if (myGen !== generationRef.current) return;
            console.warn('[PermissionsContext] live fetch failed, retaining last-known permissions:', err?.message || err);
            setError(err?.message || 'failed_to_load_permissions');
        } finally {
            if (myGen === generationRef.current) {
                lastFetchAtRef.current = Date.now();
                setLoading(false);
                setIsReady(true);
            }
        }
    }, [userId]);

    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    useEffect(() => {
        if (!userId) return undefined;
        const onFocus = () => {
            if (Date.now() - lastFetchAtRef.current < FOCUS_REFRESH_MIN_INTERVAL_MS) return;
            loadPermissions();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [userId, loadPermissions]);

    const hasPermission = useCallback(
        (requiredPermissions) => {
            if (!Array.isArray(requiredPermissions)) return false;
            return requiredPermissions.every((p) => permissions.includes(p));
        },
        [permissions],
    );

    const hasAnyPermission = useCallback(
        (keys) => {
            if (!Array.isArray(keys) || keys.length === 0) return false;
            return keys.some((k) => permissions.includes(k));
        },
        [permissions],
    );

    const value = useMemo(
        () => ({
            rolePermissions: permissions,
            permissions,
            loading,
            isReady,
            error,
            hasPermission,
            hasAnyPermission,
            hasAllPermissions: hasPermission,
            refresh: loadPermissions,
        }),
        [permissions, loading, isReady, error, hasPermission, hasAnyPermission, loadPermissions],
    );

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};
